/**
 * EJ Health V2 design tokens — Good Schools Guide-inspired editorial system,
 * tinted toward a clinical/wellness palette.
 *
 * Do NOT import LJV2_* or the main-site CORE_* tokens here. EJ Health V2 has its own token
 * system, intentionally distinct from LJ V2 and the main HKEJ V2 themes.
 */

export const EJHEALTH_COLORS = {
  // brand — sampled directly from the 信健康 logo
  // primary cyan-teal = the strokes of 信 and 健 (#1888A8)
  // accent green = 康 + the running figure (#10A860)
  primary: "#1888A8",
  primaryDark: "#0E6985",
  primarySoft: "rgba(24,136,168,0.06)",
  primaryRule: "rgba(24,136,168,0.22)",

  accent: "#10A860",
  accentDark: "#0C8A4F",
  accentSoft: "rgba(16,168,96,0.08)",

  // text
  dark: "#1A1F22",
  textBody: "#3A3A3A",
  textMuted: "#6E7378",
  textOnDark: "#FFFFFF",
  textOnDarkMuted: "rgba(255,255,255,0.65)",

  // surfaces
  surface: "#FFFFFF",
  surfaceAlt: "#F1F6F8",
  bgFooter: "#FFFFFF",

  // dividers
  divider: "rgba(26,31,34,0.10)",
  dividerStrong: "rgba(26,31,34,0.20)",
  dividerOnDark: "rgba(255,255,255,0.50)",

  // overlays
  durationChipBg: "rgba(14,105,133,0.85)",
  playButtonBg: "#10A860",
} as const;

export const EJHEALTH_FONT = {
  body: `var(--font-ejhealthv2-body), "Noto Sans TC", system-ui, sans-serif`,
  condensed: `var(--font-ejhealthv2-condensed), var(--font-ejhealthv2-tc), "Noto Sans TC", system-ui, sans-serif`,
  display: `var(--font-ejhealthv2-condensed), var(--font-ejhealthv2-tc), "Noto Sans TC", system-ui, sans-serif`,
} as const;

/**
 * Reusable type recipes. Spread into a `style={{}}` to apply.
 * Headings are sans-bold-condensed with tight negative tracking — magazine
 * editorial voice, but readable in Traditional Chinese via Noto Sans TC fallback.
 */
export const EJHEALTH_TYPE = {
  eyebrow: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 12,
    lineHeight: "16px",
    fontWeight: 700,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  },
  eyebrowLg: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 700,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
  },
  navLink: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
  },
  h1Hero: {
    fontFamily: EJHEALTH_FONT.display,
    fontSize: 56,
    lineHeight: "60px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  h2: {
    fontFamily: EJHEALTH_FONT.display,
    fontSize: 36,
    lineHeight: "40px",
    fontWeight: 700,
    letterSpacing: "-0.015em",
  },
  h3Card: {
    fontFamily: EJHEALTH_FONT.display,
    fontSize: 22,
    lineHeight: "28px",
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  cardTitleSm: {
    fontFamily: EJHEALTH_FONT.body,
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: 700,
    letterSpacing: "-0.005em",
  },
  body: {
    fontFamily: EJHEALTH_FONT.body,
    fontSize: 16,
    lineHeight: "26px",
    fontWeight: 400,
  },
  meta: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 12,
    lineHeight: "16px",
    fontWeight: 500,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
  button: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 13,
    lineHeight: "16px",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
  },
  numeral: {
    fontFamily: EJHEALTH_FONT.condensed,
    fontSize: 48,
    lineHeight: "48px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
} as const;
